import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import './styles/App.css';
import './styles/ProcedureFlow.css';
import Layout from './components/Layout';
import ProcessingIndicator from './components/ProcessingIndicator';
import ErrorFocusManager from './components/ErrorFocusManager';
import { AdminRoute, ProtectedRoute, SystemRoute } from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { defaultRouteForRole } from './utils/authRouting';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const PatientsList = lazy(() => import('./pages/PatientsList'));
const CreatePatient = lazy(() => import('./pages/CreatePatient'));
const ViewPatient = lazy(() => import('./pages/ViewPatient'));
const EditPatient = lazy(() => import('./pages/EditPatient'));
const ProceduresList = lazy(() => import('./pages/ProceduresList'));
const NewProcedure = lazy(() => import('./pages/NewProcedure'));
const ProcedureDetails = lazy(() => import('./pages/ProcedureDetails'));
const ProfessionalAvailability = lazy(() => import('./pages/ProfessionalAvailability'));
const IndicesRules = lazy(() => import('./pages/IndicesRules'));
const ProcedureReport = lazy(() => import('./pages/ProcedureReport'));
const HonorariesReport = lazy(() => import('./pages/HonorariesReport'));
const HonorarySettlement = lazy(() => import('./pages/HonorarySettlement'));
const SalesReport = lazy(() => import('./pages/SalesReport'));
const AnalyticsReport = lazy(() => import('./pages/AnalyticsReport'));
const Registrations = lazy(() => import('./pages/Registrations'));
const Payments = lazy(() => import('./pages/Payments'));
const HonoraryReceipts = lazy(() => import('./pages/HonoraryReceipts'));
const Login = lazy(() => import('./pages/Login'));
const Users = lazy(() => import('./pages/Users'));
const SystemTools = lazy(() => import('./pages/SystemTools'));

const AdminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AdminRoute>{children}</AdminRoute>
);

const SystemOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SystemRoute>{children}</SystemRoute>
);

const Home: React.FC = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Dashboard />;
  return <Navigate to={defaultRouteForRole(user.role)} replace />;
};

const RouteLoading = () => (
  <div className="page-loading"><span className="loading-spinner" />Carregando módulo...</div>
);

const ApplicationRoutes: React.FC = () => (
  <Suspense fallback={<RouteLoading />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/pacientes" element={<PatientsList />} />
      <Route path="/pacientes/novo" element={<CreatePatient />} />
      <Route path="/pacientes/:id" element={<ViewPatient />} />
      <Route path="/pacientes/:id/editar" element={<EditPatient />} />

      <Route path="/procedimentos" element={<ProceduresList />} />
      <Route path="/procedimentos/novo" element={<AdminOnly><NewProcedure /></AdminOnly>} />
      <Route path="/procedimentos/:id" element={<ProcedureDetails />} />
      <Route path="/agenda-profissionais" element={<ProfessionalAvailability />} />
      <Route path="/indices" element={<AdminOnly><IndicesRules /></AdminOnly>} />

      <Route path="/cadastros" element={<AdminOnly><Registrations /></AdminOnly>} />
      <Route path="/usuarios" element={<AdminOnly><Users /></AdminOnly>} />
      <Route path="/sistema" element={<SystemOnly><SystemTools /></SystemOnly>} />
      <Route path="/relatorios/procedimentos" element={<AdminOnly><ProcedureReport /></AdminOnly>} />
      <Route path="/relatorios/honorarios" element={<AdminOnly><HonorariesReport /></AdminOnly>} />
      <Route path="/relatorios/honorarios/baixa/:professionalId" element={<AdminOnly><HonorarySettlement /></AdminOnly>} />
      <Route path="/relatorios/vendas" element={<AdminOnly><SalesReport /></AdminOnly>} />
      <Route path="/relatorios/analises" element={<AdminOnly><AnalyticsReport /></AdminOnly>} />
      <Route path="/pagamentos" element={<AdminOnly><Payments /></AdminOnly>} />
      <Route path="/recibos-honorarios" element={<AdminOnly><HonoraryReceipts /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <ProcessingIndicator />
        <ErrorFocusManager />
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={(
                <ProtectedRoute>
                  <Layout>
                    <ApplicationRoutes />
                  </Layout>
                </ProtectedRoute>
              )}
            />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
}

export default App;
